// 서비스 페이지 데이터. 출처: 회사소개자료(국문) 3장 "핵심 서비스", HMM 제안서 "주요 사업 내용"
import type { Lang } from "@/lib/useLang";
import imgSatcom from "@/assets/images/site/satcom.jpg";
import imgPatch from "@/assets/images/site/patch-panel.jpg";
import imgAp from "@/assets/images/site/access-point.jpg";
import imgDaq from "@/assets/images/site/daq.jpg";
import imgUps from "@/assets/images/site/ups.jpg";
import imgPc from "@/assets/images/site/pc-setup.jpg";

// 순서는 galleryCaptions(ko/en)와 1:1로 맞춰야 함
export const SITE_PHOTOS = [imgSatcom, imgPatch, imgAp, imgDaq, imgUps, imgPc];

type Block = { title: string; desc: string; items: { title: string; desc: string }[] };

export const SERVICES: Record<
  Lang,
  {
    eyebrow: string;
    title: string;
    desc: string;
    seoTitle: string;
    seoDescription: string;
    blocks: Block[];
    scopeTitle: string;
    scopeDesc: string;
    scope: { area: string; detail: string }[];
    processTitle: string;
    process: { title: string; desc: string }[];
    partnershipTitle: string;
    partnershipDesc: string;
    partnership: { title: string; desc: string }[];
    historyTitle: string;
    historyDesc: string;
    historyItems: { title: string; desc: string }[];
    areaTitle: string;
    areaDomestic: { title: string; desc: string; ports: string };
    areaGlobal: { title: string; desc: string };
    galleryTitle: string;
    galleryDesc: string;
    galleryCaptions: string[];
  }
> = {
  ko: {
    eyebrow: "SERVICES",
    title: "선박 IT 유지보수 · 네트워크 구축 통합 서비스",
    desc: "선박 전산장비 유지보수부터 선박 네트워크 구축, 주요 시스템 커미셔닝까지. 선박 내 서버 · PC · 네트워크 · 보안 시스템의 생애주기 전반을 관리해 24시간 365일 안정적인 운영을 보장합니다.",
    seoTitle: "선박 IT 유지보수 · 선박 네트워크 구축 · 커미셔닝 서비스",
    seoDescription: "선박 IT 유지보수 전문 오션커넥트. 선박 전산장비 유지보수(서버·PC·네트워크·방화벽·UPS), 선박 네트워크 구축(선박 인터넷·와이파이), 위성통신·CCTV·NMS 커미셔닝, 신조·인수선 IT 구축. 국내 주요 항만 방선과 해외 원격 지원.",
    blocks: [
      {
        title: "선박 IT 유지보수 · 통합 관리",
        desc: "사전 예방부터 사후 관리까지, 원스톱 IT 관리",
        items: [
          { title: "예방 점검", desc: "정기적인 시스템 상태 점검, 보안 패치 관리, 성능 모니터링을 통해 장애를 사전에 예방합니다." },
          { title: "장애 대응", desc: "24/7 헬프데스크 운영, 원격 지원 및 긴급 방문 서비스를 통해 신속하고 정확하게 문제를 해결합니다." },
          { title: "IT 자산 관리", desc: "하드웨어 · 소프트웨어 현황 관리, 라이선스 최적화, 정기 리포트 제공으로 체계적인 자산 관리를 지원합니다." },
        ],
      },
      {
        title: "선박 네트워크 구축 및 컨설팅",
        desc: "보안을 고려한 최적화된 설계(Security by Design)와 안정적인 구축",
        items: [
          { title: "네트워크 설계", desc: "선박의 복잡한 구조와 전파 간섭 환경을 정밀 분석하여 유무선 네트워크와 보안 아키텍처를 수립합니다. 중요 시스템 보호를 위한 망분리까지 반영합니다." },
          { title: "인프라 구축", desc: "고품질 장비 설치, 케이블링, 선박 와이파이(무선 메시 Wi-Fi) 구성과 테스트로 선내 어디서나 안정적인 선박 인터넷 환경을 만듭니다." },
          { title: "기술 컨설팅", desc: "노후 장비 교체, 시스템 확장, 네트워크 분리 등 최신 기술 트렌드를 반영한 자문을 제공합니다." },
        ],
      },
      {
        title: "주요 시스템 커미셔닝",
        desc: "선박의 핵심 IT 시스템을 선박 환경에 맞춰 최적화하여 설치하고, 다른 장비와 완벽하게 연동되도록 설정 · 테스트합니다.",
        items: [
          { title: "위성통신 시스템", desc: "VSAT, FBB, IRIDIUM, STARLINK, LTE" },
          { title: "비상통신 시스템", desc: "CITADEL, 긴급 통신장비" },
          { title: "영상감시 시스템", desc: "선박용 CCTV, NVR" },
          { title: "네트워크 관리 시스템", desc: "NMS, RMM, 방화벽" },
        ],
      },
    ],
    scopeTitle: "통합 IT 유지보수 운영 범위",
    scopeDesc: "단순한 선박 유지보수를 넘어 선사의 '선박 IT 팀'으로 수행합니다. 현재 200여 척에 동일한 체계를 제공하고 있습니다.",
    scope: [
      { area: "선박 이메일", detail: "E-Mail Sharing S/W 공급 · 라이선스 · Outlook 운영/장애 지원 · 데이터 백업 관리" },
      { area: "선박 백신 · 보안", detail: "엔드포인트 백신 통합 운영 · 육상 관리 서버 운용 · 백신 자동 배포" },
      { area: "네트워크 · 방화벽", detail: "OT/IT 망 구분 · 방화벽 정책 관리 · 네트워크 장애 지원 · 신규 장비 연동(DAQ 등)" },
      { area: "PC · 주변기기", detail: "선박 전산장비 유지보수 — 서버 · PC · 모니터 · 프린터 · NAS · UPS 통합 관리" },
      { area: "위성통신", detail: "FBB · IRIDIUM · VSAT 장애 1차 대응 · 설치/커미셔닝" },
      { area: "방선 서비스", detail: "정기 방선 점검 · 입항 연계 긴급 방선 · 해외 출장 지원" },
      { area: "신조 · 인수선 구축", detail: "선내 OT/IT 네트워크 구축 · 통합 · PC · 주변기기 설치" },
      { area: "기자재 · 선용품 공급", detail: "선박 IT 기자재 및 선용품 공급 (선용품공급업 등록)" },
    ],
    processTitle: "서비스 운영 프로세스",
    process: [
      { title: "접수 및 요구사항 분석", desc: "전담 엔지니어를 배정하여 고객의 현재 시스템 환경과 비즈니스 목표를 심층 분석하고 명확한 과업 목표를 설정합니다." },
      { title: "맞춤 솔루션 설계 및 제안", desc: "기술 타당성을 검토하여 안정성과 효율성이 검증된 최적의 솔루션을 조합해 상세 제안서와 견적을 제출합니다." },
      { title: "실행 및 프로젝트 관리", desc: "수립된 계획에 따라 솔루션을 구축하고, 정기적인 진행 상황 공유와 리스크 관리로 일정과 품질을 준수합니다." },
      { title: "검수 및 안정화", desc: "최종 성능 테스트와 사용자 교육으로 완벽한 이관을 지원하며, 안정화 기간 동안 집중 모니터링합니다." },
      { title: "지속적인 지원 및 파트너십", desc: "정기 유지보수와 상세 리포트, 지속적인 개선 제안으로 고객의 성공을 함께하는 장기 파트너가 됩니다." },
    ],
    partnershipTitle: "단순 계약을 넘어선 파트너십",
    partnershipDesc: "선사 주도로 신규 IT 기기가 도입될 때 가장 큰 어려움은 기존 시스템과의 연동 및 보안 설정입니다. 오션커넥트는 고객사의 일원처럼 방화벽 정책 재설정, 기존 단말기와의 네트워크 연동 등 복잡한 기술 과업을 함께 검토하고 해결책을 제시합니다.",
    partnership: [
      { title: "상세한 단계별 가이드", desc: "엔지니어 방문이 어려운 경우에도 설치부터 설정까지 스크린샷을 포함한 단계별 맞춤 가이드를 제작해 IT 비전문가도 쉽게 따라 할 수 있게 합니다." },
      { title: "다양한 장비 · 솔루션 커버", desc: "네트워크, 방화벽, 서버 등 다양한 장비와 소프트웨어에 대한 설치 및 문제 해결 가이드를 보유하고 있습니다." },
      { title: "원격 지원과의 연계", desc: "가이드만으로 해결이 어려운 경우 원격 지원을 연계하여 엔지니어가 실시간으로 상황을 파악하고 해결합니다." },
    ],
    historyTitle: "서비스 이력 관리",
    historyDesc: "모든 기술 지원 및 유지보수 내역을 전용 관리 시스템에 체계적으로 기록하고 데이터베이스화합니다.",
    historyItems: [
      { title: "신속하고 정확한 대응", desc: "축적된 데이터를 바탕으로 과거의 문제 해결 과정과 노하우를 활용하여, 유사 문제 발생 시 더욱 신속하게 대응합니다." },
      { title: "정기 보고서를 통한 투명한 성과 공유", desc: "월간 또는 연간 단위로 선박별 장애 처리 현황, 원인 분석, 소요 시간, 예방 조치 제안이 포함된 리포트를 제공합니다." },
      { title: "효율적인 IT 자산 운영 지원", desc: "고객사는 리포트를 통해 IT 자산 운영 현황을 한눈에 파악하고, 향후 IT 투자 및 운영 계획을 효율적으로 수립할 수 있습니다." },
    ],
    areaTitle: "서비스 지역",
    areaDomestic: { title: "국내 주요 항만 신속 대응 체계", desc: "본사가 위치한 부산을 거점으로 국내 주요 항만에 엔지니어가 신속하게 방문 지원합니다. 긴급 장애 발생 시에도 빠른 초동 조치로 운항 지연 리스크를 최소화합니다.", ports: "부산 · 울산 · 마산 · 광양 · 여수 · 목포 · 군산 · 보령 · 서산 · 평택 · 인천 · 포항 · 동해 · 삼척" },
    areaGlobal: { title: "전 세계를 포괄하는 글로벌 서비스 네트워크", desc: "전 세계 어느 항구에 기항하더라도 원격 지원, 맞춤형 설치 가이드, 필요 시 출장 작업으로 선박의 위치에 구애받지 않는 서비스를 제공합니다. 해외 조선소 신조 방선 151회를 수행했습니다." },
    galleryTitle: "현장 수행 실적",
    galleryDesc: "실제 관리 선박의 방선 작업 현장입니다. 방선 점검, 위성장비 커미셔닝, 방선 수리, 신조 · 인수선 구축을 수행합니다.",
    galleryCaptions: ["위성통신 커미셔닝", "선내 네트워크 패치 · 라벨링", "선내 무선 AP 설치", "Daq (Data Acquisition)", "UPS 설치 · 교체", "업무용 PC 설치 · 설정"],
  },
  en: {
    eyebrow: "SERVICES",
    title: "Vessel IT Maintenance & Network Build — Integrated Services",
    desc: "From onboard IT equipment maintenance to ship network build and system commissioning — we manage the entire lifecycle of servers, PCs, networks, and security systems on board to guarantee stable operation 24 hours a day, 365 days a year.",
    seoTitle: "Vessel IT Maintenance · Ship Network Build · Commissioning Services",
    seoDescription: "Integrated maintenance of onboard IT equipment (servers, PCs, networks, firewalls, UPS), vessel wired/wireless network design and build, satellite/CCTV/NMS commissioning, and newbuild/takeover IT setup. Onboard service visits at major Korean ports and remote IT support worldwide.",
    blocks: [
      {
        title: "Vessel IT Maintenance & Integrated Management",
        desc: "One-stop IT management from prevention to after-care",
        items: [
          { title: "Preventive Inspection", desc: "Regular system checks, security patch management, and performance monitoring prevent faults before they occur." },
          { title: "Fault Response", desc: "A 24/7 helpdesk, remote support, and emergency visits resolve problems quickly and accurately." },
          { title: "IT Asset Management", desc: "Hardware and software inventory, license optimization, and regular reports support systematic asset management." },
        ],
      },
      {
        title: "Ship Network Build & Consulting",
        desc: "Security-by-design architecture and reliable deployment",
        items: [
          { title: "Network Design", desc: "We analyze the vessel's complex structure and RF interference to design wired/wireless networks and security architecture, including network separation for critical systems." },
          { title: "Infrastructure Build", desc: "High-quality equipment installation, cabling, and vessel Wi-Fi (wireless mesh) configuration and testing for reliable ship internet throughout the vessel." },
          { title: "Technical Consulting", desc: "Advice reflecting the latest technology trends — legacy replacement, system expansion, and network separation." },
        ],
      },
      {
        title: "System Commissioning",
        desc: "We install the vessel's core IT systems optimized for onboard conditions and configure and test them to work seamlessly with other equipment.",
        items: [
          { title: "Satellite Communication", desc: "VSAT, FBB, IRIDIUM, STARLINK, LTE" },
          { title: "Emergency Communication", desc: "CITADEL, emergency communication equipment" },
          { title: "Video Surveillance", desc: "Marine CCTV, NVR" },
          { title: "Network Management", desc: "NMS, RMM, firewall" },
        ],
      },
    ],
    scopeTitle: "Scope of Integrated IT Maintenance",
    scopeDesc: "We act as our clients' own vessel IT team, not just a maintenance vendor — the same system is in place on 200+ vessels today.",
    scope: [
      { area: "Vessel Email", detail: "E-Mail Sharing S/W supply · licensing · Outlook operation/support · data backup" },
      { area: "Antivirus & Security", detail: "Integrated endpoint antivirus · shore management server · automatic distribution" },
      { area: "Network & Firewall", detail: "OT/IT separation · firewall policy · network fault support · new device integration (DAQ, etc.)" },
      { area: "PCs & Peripherals", detail: "Servers · PCs · monitors · printers · NAS · UPS maintenance" },
      { area: "Satellite Communication", detail: "First-line fault response for FBB · IRIDIUM · VSAT · installation/commissioning" },
      { area: "Onboard Service", detail: "Regular onboard inspections · urgent visits at port calls · overseas travel support" },
      { area: "Newbuild & Takeover", detail: "Onboard OT/IT network build and integration · PC and peripheral installation" },
      { area: "Equipment & Ship Supply", detail: "Vessel IT equipment and ship supplies (registered ship supplier)" },
    ],
    processTitle: "Service Process",
    process: [
      { title: "Intake & Requirements Analysis", desc: "A dedicated engineer analyzes your current environment and business goals and sets clear objectives." },
      { title: "Solution Design & Proposal", desc: "We review technical feasibility and submit a detailed proposal and quotation with proven, stable solutions." },
      { title: "Implementation & Project Management", desc: "We build to plan with regular progress updates and risk management to keep schedule and quality." },
      { title: "Verification & Stabilization", desc: "Final performance tests and user training ensure a complete handover, with close monitoring during stabilization." },
      { title: "Ongoing Support & Partnership", desc: "Regular maintenance, detailed reports, and continuous improvement proposals make us a long-term partner." },
    ],
    partnershipTitle: "A partnership beyond the contract",
    partnershipDesc: "When shipping companies introduce new IT devices, the hardest part is integration with existing systems and security settings. Ocean Connect approaches these tasks as part of your team — firewall policy changes, network integration with existing terminals, and more.",
    partnership: [
      { title: "Step-by-step Guides", desc: "When an engineer cannot board, we produce tailored guides with screenshots from installation to configuration so non-IT crew can follow along." },
      { title: "Broad Equipment Coverage", desc: "Installation and troubleshooting guides for networks, firewalls, servers, and a wide range of software." },
      { title: "Linked Remote Support", desc: "When a guide is not enough, remote support lets an engineer see the situation in real time and resolve it." },
    ],
    historyTitle: "Service History Management",
    historyDesc: "Every support and maintenance case is recorded in a dedicated management system and built into a database.",
    historyItems: [
      { title: "Faster, More Accurate Response", desc: "Accumulated data and past resolutions let us respond faster when similar issues recur." },
      { title: "Transparent Regular Reports", desc: "Monthly or annual reports per vessel with fault status, root causes, time spent, and preventive recommendations." },
      { title: "Efficient IT Asset Operation", desc: "Reports give clients a clear view of IT asset status for better investment and operating plans." },
    ],
    areaTitle: "Service Coverage",
    areaDomestic: { title: "Rapid response at major Korean ports", desc: "From our Busan head office, engineers quickly board vessels at major Korean ports, minimizing delay risk with fast initial action in emergencies.", ports: "Busan · Ulsan · Masan · Gwangyang · Yeosu · Mokpo · Gunsan · Boryeong · Seosan · Pyeongtaek · Incheon · Pohang · Donghae · Samcheok" },
    areaGlobal: { title: "A global service network", desc: "Wherever your vessel calls, we support it through remote access, tailored installation guides, and overseas visits when needed — 151 newbuild visits at overseas shipyards to date." },
    galleryTitle: "On-site Work",
    galleryDesc: "Scenes from onboard work on managed vessels: inspections, satellite commissioning, repairs, and newbuild/takeover setups.",
    galleryCaptions: ["Satellite communication commissioning", "Onboard network patching & labeling", "Onboard wireless AP installation", "DAQ (Data Acquisition)", "UPS installation & replacement", "Office PC installation & setup"],
  },
};
